import {
  CompletionTargetUnit,
  fallbackLanguage,
  goalInboxID,
  goalOtherID,
  Habit,
  HabitSchedule,
  IconFormNames,
  ID,
  lifeAreaOtherID,
  LocalizedString,
  NewHabit,
  NewTask,
  PlannedOccurrence,
  Reminder,
  ReminderBasedOn,
  RruleReferenceDate,
  ScheduleEntry,
  ScheduleEntryState,
  Task,
  TaskAction,
  TaskActionType,
  TaskPriorityOptions,
  TaskType,
  Timestamp,
} from '@griply/types';
import {
  coerceScheduleRrule,
  getLocalizedStringValue,
  isValidRrule,
  resolveTaskTypeForSchedules,
} from '@griply/utils';
import { endOfDay } from 'date-fns';

import { FirestoreDataConverter } from '../types/firestore';
import {
  firebaseObjectTimestampToDate,
  firebaseTimestampToDate,
} from './firebase-timestamp-to-date';
import { isValidDate } from './is-valid-date';

const knownActionTypes = Object.values(TaskActionType);

// Forward-compat: a future client may write an action type this version does not
// know. Drop the unknown action so the task renders as a plain task everywhere
// rather than reaching an unhandled render branch.
const coerceAction = (value: unknown): TaskAction | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const { type, meta } = value as { type?: unknown; meta?: { id?: unknown } };
  if (!knownActionTypes.includes(type as TaskActionType)) {
    return null;
  }
  return typeof meta?.id === 'string' ? (value as TaskAction) : null;
};

// Allocation-free local-calendar-day key. Replaces startOfDay(date).getTime()
// in the entries/completions/skips loop, which minted a Date per call — the
// dominant GC source when rehydrating long-history habits.
const localDayKey = (date: Date): number =>
  date.getFullYear() * 10_000 + date.getMonth() * 100 + date.getDate();

const coercePriority = (value: unknown): TaskPriorityOptions | null => {
  if (!value) {
    return null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  if (
    !Object.values(TaskPriorityOptions).includes(value as TaskPriorityOptions)
  ) {
    return null;
  }
  return value as TaskPriorityOptions;
};

// A stray localized-string `name` resolves via `getLanguage`, falling back to fallbackLanguage.
const coerceTaskName = (value: unknown, taskId: ID): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    if (!activeReportedLocalizedNames.has(taskId)) {
      activeReportedLocalizedNames.add(taskId);
      activeOnError?.(new Error('Localized-object task name on read'), {
        taskId,
        name: value,
      });
    }
    const resolved = getLocalizedStringValue(
      value as LocalizedString,
      activeGetLanguage?.() ?? fallbackLanguage,
    );
    if (typeof resolved === 'string') {
      return resolved;
    }
  }
  return '';
};

const coerceTaskType = (value: unknown): TaskType => {
  if (!Object.values(TaskType).includes(value as TaskType)) {
    return TaskType.Task;
  }
  return value as TaskType;
};

const coerceIconName = (value: unknown): IconFormNames | null => {
  if (!value) {
    return null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  if (!Object.values(IconFormNames).includes(value as IconFormNames)) {
    return null;
  }
  return value as IconFormNames;
};

const isKnownEntryState = (value: unknown): value is ScheduleEntryState =>
  Object.values(ScheduleEntryState).includes(value as ScheduleEntryState);

const coerceCompletionTargetUnit = (value: unknown): CompletionTargetUnit =>
  Object.values(CompletionTargetUnit).includes(value as CompletionTargetUnit)
    ? (value as CompletionTargetUnit)
    : CompletionTargetUnit.Day;

// 'completionDate' is the backend's stale alias for LastCompletion.
const coerceRruleReferenceDate = (value: unknown): RruleReferenceDate => {
  if (value === 'completionDate') {
    return RruleReferenceDate.LastCompletion;
  }
  return Object.values(RruleReferenceDate).includes(value as RruleReferenceDate)
    ? (value as RruleReferenceDate)
    : RruleReferenceDate.StartDate;
};

// A future client may write an unknown `basedOn` or malformed entries; an absent field must stay null so
// the legacy `reminderTime` can still surface, while an empty array means "explicitly no reminders".
const coerceReminders = (value: unknown): Reminder[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.reduce<Reminder[]>((reminders, entry) => {
    if (!entry || typeof entry !== 'object') {
      return reminders;
    }
    const { id, offset, basedOn } = entry as Record<string, unknown>;
    if (typeof offset !== 'number' || !Number.isFinite(offset)) {
      return reminders;
    }
    reminders.push({
      id: typeof id === 'string' ? id : null,
      offset,
      basedOn: Object.values(ReminderBasedOn).includes(
        basedOn as ReminderBasedOn,
      )
        ? (basedOn as ReminderBasedOn)
        : ReminderBasedOn.Start,
    });
    return reminders;
  }, []);
};

export type TaskConverterOnError = (
  error: Error,
  context: { taskId: ID; rrule?: string; name?: unknown },
) => void;

export type TaskConverterOptions = {
  onError?: TaskConverterOnError;
  // Current UI language (e.g. 'nl-NL'); read per conversion so a language switch applies immediately.
  getLanguage?: () => string;
};

// Sync set-before-use by the factory wrapper; safe because converters never interleave.
let activeOnError: TaskConverterOnError | undefined;
let activeGetLanguage: (() => string) | undefined;
let activeReportedInvalidRrules = new Set<string>();
let activeReportedLocalizedNames = new Set<ID>();

const baseConverter: FirestoreDataConverter<Task> = {
  fromFirestore: (snapshot) => {
    const rawData = firebaseObjectTimestampToDate(snapshot.data());
    const data = rawData as Record<string, unknown>;
    data.id = snapshot.id;
    const knownType = coerceTaskType(data.type);
    data.type = knownType;

    let parsedData: Partial<Task> = {
      type: TaskType.Task,
      createdAt: (data.createdAt as Date | undefined) ?? new Date(),
      updatedAt: (data.updatedAt as Date | undefined) ?? new Date(),
      startDate:
        ((data.startDate as Date | undefined)
          ? (data.startDate as Date)
          : (data.createdAt as Date | undefined)) ?? new Date(),
      reminderTime: (data.reminderTime as number | null | undefined) ?? null,
      name: coerceTaskName(data.name, snapshot.id),
      reminders: coerceReminders(data.reminders),
      iconName: coerceIconName(data.iconName),
      parentIds: (data.parentIds as ID[])?.length
        ? (data.parentIds as ID[])
        : null,
      priority: coercePriority(data.priority),
      action: coerceAction(data.action),
      schedules: null,
    };

    if (
      [TaskType.Habit, TaskType.Repeating].includes(knownType) &&
      (data.schedules as HabitSchedule[] | undefined)?.length
    ) {
      const taskCreatedAt = parsedData.createdAt ?? new Date();

      const schedules = (data.schedules as HabitSchedule[])
        .map<HabitSchedule>((schedule) => {
          const validEntries = (schedule.entries ?? []).filter(
            (entry) =>
              isValidDate(entry.date) && isKnownEntryState(entry.state),
          );
          const validCompletions = (schedule.completions ?? []).filter(
            (date): date is Timestamp => isValidDate(date),
          );
          const validSkips = (schedule.skips ?? []).filter(
            (date): date is Timestamp => isValidDate(date),
          );
          const validPlannedOccurrences = (
            schedule.plannedOccurrences ?? []
          ).filter((occurrence): occurrence is PlannedOccurrence =>
            isValidDate(occurrence.date),
          );

          // O(1) day lookup instead of O(n×m) isSameDay scans — built only when
          // there are legacy completions/skips to migrate, so modern entries-only
          // habits skip this whole pass over their (long) history.
          const existingEntryDays =
            validCompletions.length || validSkips.length
              ? new Set(validEntries.map((entry) => localDayKey(entry.date)))
              : new Set<number>();

          const originalRrule = schedule.rrule?.format;
          const { rrule: coercedRrule } = originalRrule
            ? coerceScheduleRrule(originalRrule)
            : { rrule: originalRrule };
          if (
            originalRrule &&
            !isValidRrule(originalRrule, { hasCount: !!schedule.frequency }) &&
            !activeReportedInvalidRrules.has(originalRrule)
          ) {
            activeReportedInvalidRrules.add(originalRrule);
            activeOnError?.(new Error('Invalid schedule rrule on read'), {
              taskId: snapshot.id,
              rrule: originalRrule,
            });
          }

          return {
            ...schedule,
            rrule: schedule.rrule
              ? {
                  ...schedule.rrule,
                  format: coercedRrule as string,
                  referenceDate: coerceRruleReferenceDate(
                    schedule.rrule.referenceDate,
                  ),
                }
              : schedule.rrule,
            startDate: isValidDate(schedule.startDate)
              ? schedule.startDate
              : taskCreatedAt,
            endDate: firebaseTimestampToDate(schedule.endDate),
            entries: (
              [
                ...validEntries,
                ...validCompletions.map((date) =>
                  existingEntryDays.has(localDayKey(date))
                    ? undefined
                    : {
                        id: `${date}`,
                        date: date,
                        state: ScheduleEntryState.Complete,
                        value: 1,
                      },
                ),
                ...validSkips.map((date) =>
                  existingEntryDays.has(localDayKey(date))
                    ? undefined
                    : {
                        id: `${date}`,
                        date: date,
                        state: ScheduleEntryState.Skip,
                        value: 1,
                      },
                ),
              ] as ScheduleEntry[]
            )
              .filter(Boolean)
              .sort(
                (entryA, entryB) =>
                  entryA.date.getTime() - entryB.date.getTime(),
              ),
            completions: validCompletions.length ? validCompletions : null,
            skips: validSkips.length ? validSkips : null,
            // dedupe by *local* day (UTC-ms keying broke on negative-UTC endOfDay rolls), LAST wins — newest pin is the latest user action
            plannedOccurrences: [
              ...new Map(
                validPlannedOccurrences.map((occurrence) => [
                  localDayKey(occurrence.date),
                  occurrence,
                ]),
              ).values(),
            ],
            completionTarget: {
              count:
                typeof schedule.completionTarget?.count === 'number'
                  ? schedule.completionTarget.count
                  : 1,
              unit: coerceCompletionTargetUnit(schedule.completionTarget?.unit),
            },
          };
        })
        // A schedule with neither an rrule nor a frequency cannot produce a single occurrence, and everything that
        // reads one — planning, the timeframe grid, the label — reads `rrule.format`. Drop it here so that shape
        // never reaches the apps or the widget.
        .filter(
          (schedule) => !!schedule.rrule?.format || !!schedule.frequency?.count,
        );

      // No schedule left to define due-ness: the document is a plain task, whatever its stored `type` says.
      if (schedules.length) {
        parsedData = parsedData as unknown as Habit;
        parsedData.type = knownType as TaskType.Habit;
        // Habits and repeating tasks must never carry a `deadlineDeadline` — their schedule defines due-ness.
        // Stale values can survive a Todo → Habit conversion and misclassify the task as overdue.
        parsedData.deadlineDeadline = null;
        parsedData.iconName =
          coerceIconName(data.iconName) ?? IconFormNames.Repeat;
        parsedData.schedules = schedules;
      }
    }

    return {
      ...data,
      ...parsedData,
    } as Task;
  },
  toFirestore: (task) => task,
};

export const createTaskConverter = ({
  onError,
  getLanguage,
}: TaskConverterOptions = {}): FirestoreDataConverter<Task> => {
  const reportedInvalidRrules = new Set<string>();
  const reportedLocalizedNames = new Set<ID>();

  return {
    fromFirestore: (snapshot) => {
      activeOnError = onError;
      activeGetLanguage = getLanguage;
      activeReportedInvalidRrules = reportedInvalidRrules;
      activeReportedLocalizedNames = reportedLocalizedNames;
      return baseConverter.fromFirestore(snapshot);
    },
    toFirestore: (task) => task,
  };
};

// No language and no error reporting: apps use createTaskConverter({ onError, getLanguage }).
export const defaultTaskConverter = createTaskConverter();

const excludedGoalIds = [goalInboxID, goalOtherID];

export const newTaskConverter = (task: NewTask) => {
  const deadline = task.endStrategy?.deadline;
  const isScheduledType = !!task.schedules?.length;

  return {
    ...task,
    type: resolveTaskTypeForSchedules(task.type, task.schedules),
    // habits/repeating tasks must never carry a deadlineDeadline; the
    // schedule defines due-ness.
    deadlineDeadline: isScheduledType
      ? null
      : (task as { deadlineDeadline?: Timestamp | null }).deadlineDeadline ??
        null,
    iconName: (task as NewHabit).iconName ?? null,
    createdAt: new Date(),
    startDate: task.startDate ? new Date(task.startDate) : new Date(),
    duration: (task as { duration?: number | null }).duration ?? null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    goalId: excludedGoalIds.includes(task.goalId!) ? null : task.goalId ?? null,
    lifeAreaId:
      task.lifeAreaId === lifeAreaOtherID ? null : task.lifeAreaId ?? null,
    reminders: task.reminders ?? null,
    // Any `reminders` value (incl. `[]`) is authoritative, so the legacy value can't resurface.
    reminderTime: task.reminders != null ? null : task.reminderTime ?? null,
    priority: task.priority ?? null,
    endStrategy: {
      deadline: deadline ? endOfDay(deadline) : null,
      completionCount: task.endStrategy?.completionCount ?? null,
    },
    schedules: task.schedules?.length
      ? task.schedules.map((schedule) => ({
          ...schedule,
          startDate: schedule.startDate
            ? new Date(schedule.startDate)
            : new Date(),
          endDate: schedule.endDate ? new Date(schedule.endDate) : null,
          entries: schedule.entries?.map((entry) => ({
            ...entry,
            date: entry.date ? new Date(entry.date) : null,
          })),
        }))
      : null,
  };
};
